import { useMutation } from 'react-query';

import { postV2ApiCompanyIdVerificationsOfPayees } from 'src/client/spendesk-api';
import type {
  PostV2ApiCompanyIdVerificationsOfPayeesRequest,
  PostV2ApiCompanyIdVerificationsOfPayeesResponseOK,
} from 'src/client/spendesk-api-types';
import { appQueryClient } from 'src/core/api/client';
import { useCompanyId } from 'src/core/modules/app/hooks/useCompanyId';

export type VerificationOfPayeePayload =
  PostV2ApiCompanyIdVerificationsOfPayeesRequest['body'];

export type VerificationOfPayeeResult =
  PostV2ApiCompanyIdVerificationsOfPayeesResponseOK['data'];

export const fetchVerificationOfPayee = async (
  companyId: string,
  payload: VerificationOfPayeePayload,
): Promise<VerificationOfPayeeResult> => {
  const cacheKey = getVerificationOfPayeeQueryCacheKey({
    iban: payload.iban,
    name: payload.name,
  });

  if (!payload.refresh) {
    const cachedData =
      appQueryClient.getQueryData<VerificationOfPayeeResult>(cacheKey);
    if (cachedData) {
      return cachedData;
    }
  }

  const response = await postV2ApiCompanyIdVerificationsOfPayees({
    path: {
      companyId,
    },
    body: {
      iban: payload.iban,
      name: payload.name,
      refresh: true,
      persist: payload.persist,
    },
  });

  if (response.statusCode !== 200) {
    throw new Error('Failed to verify payee');
  }

  const data = response.body.data;
  if (!data) {
    throw new Error('Failed to verify payee');
  }

  updateVerificationOfPayeeQueryCache(
    { iban: payload.iban, name: payload.name },
    data,
  );

  return data;
};

type VerificationOfPayeeQueryCacheKey =
  `${'verificationOfPayee'}-${string}-${string}`;

const getVerificationOfPayeeQueryCacheKey = ({
  iban,
  name,
}: {
  iban: string;
  name: string;
}): VerificationOfPayeeQueryCacheKey => {
  return `verificationOfPayee-${iban}-${name}`;
};
const updateVerificationOfPayeeQueryCache = (
  { iban, name }: { iban: string; name: string },
  result: VerificationOfPayeeResult,
) => {
  const cacheKey = getVerificationOfPayeeQueryCacheKey({ iban, name });
  appQueryClient.setQueryData(cacheKey, result);
};

export const getCurrentVerificationOfPayeeFromCache = ({
  iban,
  name,
}: {
  iban: string;
  name: string;
}): VerificationOfPayeeResult => {
  const cacheKey = getVerificationOfPayeeQueryCacheKey({ iban, name });
  const cachedData =
    appQueryClient.getQueryData<VerificationOfPayeeResult>(cacheKey);

  if (!cachedData) {
    throw new Error('Verification of payee result not found in cache');
  }

  return cachedData;
};

export const useVerificationOfPayeeQuery = () => {
  const companyId = useCompanyId();

  return useMutation((payload: VerificationOfPayeePayload) =>
    fetchVerificationOfPayee(companyId, payload),
  );
};

export const useVerificationOfPayeeCheck = () => {
  const companyId = useCompanyId();

  return (payload: VerificationOfPayeePayload) =>
    fetchVerificationOfPayee(companyId, payload);
};
